import {OtsDateTime} from "../components/OtsDateTime";
import {AppAccessMode, AppAccessTarget} from "@ots-frontend/ots-data";
import {LinkIcon, QrCodeIcon, TrashIcon} from "@heroicons/react/20/solid";
import {OtsActionButton, OtsIconActionButton} from "../components/OtsButton";
import {StatusPill} from "../components/StatusPill";
import {canDeliverAppAccessTarget, getAppAccessApp, getAppAccessAppLabel, getAppAccessIdentityLabel, getAppAccessTargetDeliveryStatus} from "./app-access-ui";

export type AppAccessTargetCardProps = {
  target: AppAccessTarget;
  mode: AppAccessMode;
  canDistribute?: boolean;
  canRevoke?: boolean;
  onCopyLink: (target: AppAccessTarget) => void | Promise<void>;
  onExportQr: (target: AppAccessTarget) => void | Promise<void>;
  onRevoke: (target: AppAccessTarget) => void | Promise<void>;
};

/** Delivery actions and state for one app target within a package. */
export const AppAccessTargetCard = ({
  target,
  mode,
  canDistribute = true,
  canRevoke = true,
  onCopyLink,
  onExportQr,
  onRevoke,
}: AppAccessTargetCardProps) => {
  const deliveryStatus = getAppAccessTargetDeliveryStatus(target, mode);
  const canDeliver = canDeliverAppAccessTarget(target, mode);
  const app = getAppAccessApp(target.app);
  const appIdentityLabel = getAppAccessIdentityLabel(target);

  return (
    <article className="rounded-xl border border-[var(--ots-outline-variant)] bg-[var(--ots-field)] p-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          {app && <img src={app.logo} alt="" className="size-11 shrink-0 rounded-xl"/>}
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <strong className="text-[var(--ots-secondary)]">{getAppAccessAppLabel(target.app)}</strong>
              <StatusPill tone={deliveryStatus.tone}>{deliveryStatus.label}</StatusPill>
            </div>
            <div className="mt-1 text-xs text-[var(--ots-text-muted)]">
              {mode === "ONE_TIME_CREDENTIAL" ? "One-time app setup" : "Reusable onboarding setup"}
              {target.regionName ? ` · ${target.regionName}` : ""}
              {target.expiresDttm != null && <> · Expires <OtsDateTime value={target.expiresDttm} /></>}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canDistribute && <OtsActionButton size="compact" variant="primary" icon={<LinkIcon/>} disabled={!canDeliver} onClick={() => void onCopyLink(target)}>Copy link</OtsActionButton>}
          {canDistribute && <OtsActionButton size="compact" icon={<QrCodeIcon/>} disabled={!canDeliver} onClick={() => void onExportQr(target)}>QR code</OtsActionButton>}
          {canRevoke && <OtsIconActionButton size="compact" variant="text" icon={<TrashIcon/>} label={`Revoke ${getAppAccessAppLabel(target.app)} setup`} disabled={target.state === "REVOKED"} onClick={() => void onRevoke(target)}/>}
        </div>
      </div>
      {target.appIdentityId ? (
        <dl className="mt-3 grid gap-x-3 gap-y-1 border-t border-[var(--ots-outline-variant)] pt-3 text-xs sm:grid-cols-[auto_minmax(0,1fr)]">
          <dt className="font-medium text-[var(--ots-text-muted)]">App Identity</dt>
          <dd className="min-w-0 text-[var(--ots-secondary)]">
            <span className="font-medium">{appIdentityLabel}</span>
            {target.appIdentityDisplayName && target.username && target.appIdentityDisplayName !== target.username
              ? <span className="text-[var(--ots-text-muted)]"> · {target.username}</span>
              : null}
          </dd>
          <dt className="font-medium text-[var(--ots-text-muted)]">Identity ID</dt>
          <dd className="break-all font-mono text-[var(--ots-text-muted)]">{target.appIdentityId}</dd>
        </dl>
      ) : (
        <p className="mt-3 border-t border-[var(--ots-outline-variant)] pt-3 text-xs text-[var(--ots-text-muted)]">
          No App Identity is required for this install-only setup.
        </p>
      )}
    </article>
  );
};
